<template>
  <CustomDashboard
    :user-id="userId"
    :dashboard="dashboard"
    :dashboard-owners="dashboardOwners"
    :export-data="exportData"
    :date-options="dateOptions"
    @handleSelectionChange="handleSelectionChange"
  />
</template>

<script>
import CustomDashboard from '@/components/CustomDashboard'
import { getData, isWidgetFilter } from '@/helpers/custom-dashboard'

export default {
  name: 'UnitEconomicsLeaderBoard',

  components: {
    CustomDashboard
  },

  props: {
    userId: Number
  },

  data () {
    return {
      context: 'dashboard',
      dashboard: DASHBOARD,
      queryLimit: 100000,
      dashboardOwners: 'robert.ludew@li.me;can.wang@li.me;jiaying.liu@li.me',
      latestValidDateForData: '',
      initialDataLoaded: false,
      dashboardOpenLogged: false,
      dashboardLoadStart: 0,
      dashboardLoadEnd: 0
    }
  },

  computed: {
    leaderboardQuery () {
      let query = `SELECT *, to_number(rank() over (order by CONTRIBUTION_PROFIT_LESS_FIXED_COSTS DESC)) as RANKING FROM ${this.dashboard.dataSource} WHERE`

      query += ` WEEK = date_trunc('week', date('${this.dashboard.widgets[2].value}'))`
      query += ` AND CONTRIBUTION_PROFIT_LESS_FIXED_COSTS IS NOT NULL`
      query += ` AND AGGREGATION = '${this.dashboard.widgets[1].value}'`

      return query
    },

    exportData () {
      return this.dashboard.widgets.filter(widget => widget.metadata && widget.metadata.type === 'table')
        .map(widget => ({ name: widget.metadata.title, data: widget.value }))
    },

    dateOptions () {
      const latestValidDate = this.latestValidDateForData ? this.$moment(this.latestValidDateForData) : this.$moment().subtract(1, 'weeks')

      return {
        disabledDate: date => (this.$moment(date).isoWeekday() !== 1 || this.$moment(date).isAfter(latestValidDate))
      }
    }
  },

  watch: {
    leaderboardQuery () {
      this.loadDashboard()
    },

    dashboard: {
      deep: true,
      handler () {
        this.setRouterQueryParams()
      }
    }
  },

  beforeCreate () {
    sessionStorage.setItem('context', 'dashboards')
  },

  async created () {
    try {
      this.dashboardLoadStart = performance.now()
      this.dashboard.widgets[0].loading = true

      this.backfillQueryParams()
      this.latestValidDateForData = await this.getLatestValidDateForData()

      if (!this.dashboard.widgets[2].value) {
        this.dashboard.widgets[2].value = this.latestValidDateForData
      }

      this.setRegionAggColumnSettings(this.dashboard.widgets[1].value)
      await this.loadDashboard()
      this.dashboard.widgets[0].loading = false
      this.initialDataLoaded = true
    } catch (error) {
      console.error(`Error while performing initial load of UE leaderboard: ${error.message}`)
    }
  },

  updated () {
    if (this.initialDataLoaded) {
      if (!this.dashboardOpenLogged) {
        this.$nextTick(() => {
          this.dashboardLoadEnd = performance.now()
          this.$customDashboardOpenLog(this.dashboard.title.name, this.dashboardLoadEnd - this.dashboardLoadStart)
          this.dashboardOpenLogged = true
        })
      }
    }
  },

  methods: {
    handleSelectionChange (i, val) {
      const widget = this.dashboard.widgets[i]

      if (widget.metadata.name && widget.metadata.name === 'REGION_AGG') {
        this.setRegionAggColumnSettings(val)
      }
    },

    setRegionAggColumnSettings (regionAgg) {
      if (regionAgg.toLowerCase() === 'region') {
        this.dashboard.widgets[0].headerColumn = REGION_AGG_HEADER_COLUMN
        this.dashboard.widgets[0].columns = REGION_AGG_COLUMNS
      } else {
        this.dashboard.widgets[0].headerColumn = SUPER_REGION_AGG_HEADER_COLUMN
        this.dashboard.widgets[0].columns = SUPER_REGION_AGG_COLUMNS
      }
    },

    async getLatestValidDateForData () {
      const query = `
        SELECT top 1 WEEK
        from ${this.dashboard.dataSource} as t1
        WHERE exists (
          SELECT *
          FROM ${this.dashboard.dataSource} as t2
          WHERE t2.CONTRIBUTION_PROFIT_LESS_FIXED_COSTS is not null and t1.WEEK = t2.WEEK
        )
        order by week desc
      `

      try {
        const result = await getData(query, this.queryLimit)

        if (result.data.error) {
          throw new Error('Failed to get data for first date of last full week')
        }

        return result.data.data[0].WEEK
      } catch (error) {
        console.error(`Error while getting first date of last full week: ${error.message}`)
        throw error
      }
    },

    setRouterQueryParams () {
      const queryParams = this.dashboard.widgets
        .filter(widget => isWidgetFilter(widget))
        .reduce((acc, cur) => {
          acc[cur.metadata.name.toLowerCase()] = cur.value ? cur.value.toString() : ''
          return acc
        }, {})

      this.$router.replace({ name: this.$route.name, query: queryParams })
    },

    backfillQueryParams () {
      Object.keys(this.$route.query).forEach(param => {
        this.dashboard.widgets
          .filter(widget => widget.metadata.name && widget.metadata.name.toLowerCase() === param.toLowerCase() && isWidgetFilter(widget))
          .forEach(widget => {
            if (this.$route.query[param]) {
              widget.value = this.$route.query[param]
            }
          })
      })
    },

    async loadDashboard () {
      this.dashboard.widgets[0].loading = true
      try {
        const result = await getData(this.leaderboardQuery, this.queryLimit)

        if (result.data.error) {
          throw new Error('Could not get data for dashboard')
        }

        this.$dashboardQueryLog(this.dashboard.title.name, result.data.queryId, this.leaderboardQuery, 'unitEconomicsLeaderboardQuery')
        this.dashboard.widgets[0].value = Object.freeze(result.data.data)
        this.dashboard.widgets[0].loading = false
      } catch (error) {
        console.error(`Error while loading dashboard: ${error.message}`)

        this.dashboard.widgets[0].value = []
        this.dashboard.widgets[0].loading = false
      }
    }
  }
}

const REGION_AGG_HEADER_COLUMN = {
  name: 'REGION',
  displayName: 'Region',
  format: 'string',
  orderType: 'normal',
  tooltip: []
}

const SUPER_REGION_AGG_HEADER_COLUMN = {
  name: 'SUPER_REGION',
  displayName: 'Super Region',
  format: 'string',
  orderType: 'normal',
  tooltip: []
}

const REGION_AGG_COLUMNS = {
  RANKING: {
    displayName: '#',
    format: 'number',
    orderType: 'normal',
    tooltip: []
  },
  SUPER_REGION: {
    displayName: 'Super Region',
    format: 'string',
    orderType: 'normal',
    tooltip: []
  },
  CORE_REGION: {
    displayName: 'Core Region',
    format: 'string',
    orderType: 'normal',
    tooltip: []
  },
  CONTRIBUTION_PROFIT_LESS_FIXED_COSTS: {
    displayName: 'City Profit',
    format: 'currencyDecimal',
    orderType: 'normal',
    tooltip: []
  },
  GROSS_MARGIN: {
    displayName: 'City Profit % GB',
    format: 'percentage',
    orderType: 'normal',
    tooltip: []
  },
  GROSS_REVENUE: {
    displayName: 'Gross Bookings',
    format: 'currencyDecimal',
    orderType: 'normal',
    tooltip: []
  },
  TRIPS: {
    displayName: 'Trips',
    format: 'number',
    orderType: 'normal',
    tooltip: []
  },
  OPERATIONAL_VEHICLES: {
    displayName: 'Operational Vehicles',
    format: 'number',
    orderType: 'normal',
    tooltip: []
  },
  RPT: {
    displayName: 'RPT',
    format: 'currencyDecimal',
    orderType: 'normal',
    tooltip: []
  },
  TVD: {
    displayName: 'TVD',
    format: 'decimal',
    orderType: 'normal',
    tooltip: []
  },
  RVD: {
    displayName: 'RVD',
    format: 'currencyDecimal',
    orderType: 'normal',
    tooltip: []
  },
  NET_DECAY_RATE: {
    displayName: 'Net Ops Decay Rate',
    format: 'percentage',
    orderType: 'normal',
    tooltip: []
  },
  LABOR_COST_PCT_GROSS: {
    displayName: 'Labor Cost % of GB',
    format: 'percentage',
    orderType: 'normal',
    tooltip: []
  },
  CITY_EBITDA: {
    displayName: 'EBITDA $',
    format: 'currencyDecimal',
    orderType: 'normal',
    tooltip: []
  },
  CITY_EBITDA_PCT_GROSS: {
    displayName: 'EBITDA % GB',
    format: 'percentage',
    orderType: 'normal',
    tooltip: []
  },
  CITY_EBITDA_PER_VEHICLE_PER_DAY: {
    displayName: 'EBITDA Per Vehicle',
    format: 'currencyDecimal',
    orderType: 'normal',
    tooltip: []
  },
  CITY_PROFIT_PER_VEHICLE_PER_DAY: {
    displayName: 'City Profit Per Vehicle',
    format: 'currencyDecimal',
    orderType: 'normal',
    tooltip: []
  }
}

const SUPER_REGION_AGG_COLUMNS = Object.keys(REGION_AGG_COLUMNS)
  .filter(key => key !== 'SUPER_REGION' && key !== 'CORE_REGION')
  .reduce((acc, key) => {
    acc[key] = REGION_AGG_COLUMNS[key]

    return acc
  }, {})

const DASHBOARD = {
  dashboardName: 'Unit Economics Leaderboard',
  title: {
    name: 'Unit Economics Leaderboard',
    isShown: false
  },
  dataSource: 'PRODUCTION_DATA.DM.NEW_UE_REPORT_WEEKLY_VIEW',
  widgets: [{
    x: 0,
    y: 0,
    w: 20,
    h: 50,
    i: 0,
    metadata: {
      name: 'UNIT_ECONOMICS_LEADERBOARD',
      title: 'Unit Economics Leaderboard',
      type: 'table',
      customField: false,
      tableType: 'vertical'
    },
    styles: {
      leftColWidth: 130
    },
    headerColumn: SUPER_REGION_AGG_HEADER_COLUMN,
    columns: SUPER_REGION_AGG_COLUMNS,
    value: [],
    loading: true
  }, {
    x: 20,
    y: 0,
    w: 4,
    h: 4,
    i: 1,
    metadata: {
      name: 'REGION_AGG',
      title: 'Region Aggregation',
      type: 'select',
      customField: true
    },
    column: 'AGGREGATION',
    value: 'Super Region',
    options: [{
      name: 'Super Region',
      value: 'Super Region'
    }, {
      name: 'Region',
      value: 'Region'
    }]
  }, {
    x: 20,
    y: 4,
    w: 4,
    h: 4,
    i: 2,
    metadata: {
      name: 'WEEK',
      title: 'Week',
      type: 'calendar',
      customField: true
    },
    column: 'WEEK',
    value: ''
  }, {
    x: 20,
    y: 8,
    w: 4,
    h: 2,
    i: 3,
    metadata: {
      type: 'report',
      customField: true
    }
  }]
}

</script>
